import PropTypes from 'prop-types';

import { BackendState, BackendStateWithData } from '../../types';
import { ChargebeeCardDetailsDTO } from '../../types/dto/ChargebeeCardDetailsDTO';
import { ChargebeeCouponDTO } from '../../types/dto/ChargebeeCouponDTO';
import { ChargebeePlanDTO } from '../../types/dto/ChargebeePlanDTO';
import { ChargebeeSubscriptionDTO } from '../../types/dto/ChargebeeSubscriptionDTO';
import { SubscriptionCreditNoteDTO } from '../../types/dto/SubscriptionCreditNoteDTO';
import { SubscriptionInvoiceDTO } from '../../types/dto/SubscriptionInvoiceDTO';
import { SubscriptionItemDTO } from '../../types/dto/SubscriptionItemDTO';

//////////////////////////////////////////////////////////////////////////////////////////
// Misc shapes
//////////////////////////////////////////////////////////////////////////////////////////
export interface SubscriptionErrorInfo {
    response: any;
    errorCode: string;
    errorMessage: string | null;
    exceptionType: string | null;
}

export class SubscriptionError extends Error {
    public subscriptionErrorInfo: SubscriptionErrorInfo;

    constructor(subscriptionErrorInfo: SubscriptionErrorInfo, ...params: any[]) {
        // Pass remaining arguments (including vendor specific ones) to parent constructor
        super(...params);

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, SubscriptionError);
        }

        if (subscriptionErrorInfo.errorMessage !== null) {
            this.message = subscriptionErrorInfo.errorMessage;
        }
        // Custom debugging information
        this.subscriptionErrorInfo = subscriptionErrorInfo;
    }
}

export interface GetActivePlans {
    readonly data: ChargebeePlanDTO[];
}

export interface GetSubscriptionItems {
    readonly data: SubscriptionItemDTO[];
}

export interface GetUserSubscriptions {
    readonly data: ChargebeeSubscriptionDTO[];
}

export interface GetPrimaryPaymentSource {
    readonly data: ChargebeeCardDetailsDTO;
}

export interface GetUserSubscriptionInvoices {
    readonly data: SubscriptionInvoiceDTO[];
}

export interface GetUserSubscriptionCreditNotes {
    readonly data: SubscriptionCreditNoteDTO[];
}

export interface UpdatePaymentSource {
    readonly data: boolean;
}

export interface GetCouponDetails {
    readonly data: ChargebeeCouponDTO;
}

export interface CreateSubscriptionV2 {
    readonly data: ChargebeeSubscriptionDTO;
}

export interface CancelSubscription {
    readonly data: ChargebeeSubscriptionDTO;
}

export interface PrintSubscriptionInvoice {
    readonly subscriptionInvoice?: Blob;
}

export interface PrintSubscriptionCreditNote {
    readonly subscriptionCreditNote?: Blob;
}

export interface GetAdyenPaymentMethods {
    readonly data?: any;
}

export interface GetActivePlansState extends BackendStateWithData<ChargebeePlanDTO[]> { }
export interface GetSubscriptionItemsState extends BackendStateWithData<SubscriptionItemDTO[]> { }
export interface GetUserSubscriptionsState extends BackendStateWithData<ChargebeeSubscriptionDTO[]> { }
export interface GetPrimaryPaymentSourceState extends BackendStateWithData<ChargebeeCardDetailsDTO> { }
export interface GetUserSubscriptionInvoicesState extends BackendStateWithData<SubscriptionInvoiceDTO[]> { }
export interface GetUserSubscriptionCreditNotesState extends BackendStateWithData<SubscriptionCreditNoteDTO[]> { }
export interface UpdatePaymentSourceState extends BackendStateWithData<boolean> { }
export interface GetCouponDetailsState extends BackendStateWithData<ChargebeeCouponDTO> { }
export interface UpdateVehicleFromDOMState extends BackendState { }
export interface CreateSubscriptionV2State extends BackendStateWithData<ChargebeeSubscriptionDTO> { }
export interface CancelSubscriptionState extends BackendStateWithData<ChargebeeSubscriptionDTO> { }
export interface PrintSubscriptionInvoiceState extends BackendStateWithData<Blob | undefined> { }
export interface PrintSubscriptionCreditNoteState extends BackendStateWithData<Blob | undefined> { }
export interface GetAdyenPaymentMethodsState extends BackendStateWithData<any> { }

export interface SubscriptionState {
    readonly getActivePlans: GetActivePlansState;
    readonly getSubscriptionItems: GetSubscriptionItemsState;
    readonly getUserSubscriptions: GetUserSubscriptionsState;
    readonly getPrimaryPaymentSource: GetPrimaryPaymentSourceState;
    readonly getUserSubscriptionInvoices: GetUserSubscriptionInvoicesState;
    readonly getUserSubscriptionCreditNotes: GetUserSubscriptionCreditNotesState;
    readonly updatePaymentSource: UpdatePaymentSourceState;
    readonly getCouponDetails: GetCouponDetailsState;
    readonly updateVehicleFromDOM: UpdateVehicleFromDOMState;
    readonly createSubscriptionV2: CreateSubscriptionV2State;
    readonly cancelSubscription: CancelSubscriptionState;
    readonly printSubscriptionInvoice: PrintSubscriptionInvoiceState;
    readonly printSubscriptionCreditNote: PrintSubscriptionCreditNoteState;
    readonly getAdyenPaymentMethods: GetAdyenPaymentMethodsState;
}

//////////////////////////////////////////////////////////////////////////////////////////
// Action shapes
//////////////////////////////////////////////////////////////////////////////////////////

export const SubscriptionActionPropTypes = PropTypes.shape({
    getActivePlansRequest: PropTypes.func.isRequired,
    getActivePlansSuccess: PropTypes.func.isRequired,
    getActivePlansFailure: PropTypes.func.isRequired,

    getSubscriptionItemsRequest: PropTypes.func.isRequired,
    getSubscriptionItemsSuccess: PropTypes.func.isRequired,
    getSubscriptionItemsFailure: PropTypes.func.isRequired,

    getUserSubscriptionsRequest: PropTypes.func.isRequired,
    getUserSubscriptionsSuccess: PropTypes.func.isRequired,
    getUserSubscriptionsFailure: PropTypes.func.isRequired,

    getPrimaryPaymentSourceRequest: PropTypes.func.isRequired,
    getPrimaryPaymentSourceSuccess: PropTypes.func.isRequired,
    getPrimaryPaymentSourceFailure: PropTypes.func.isRequired,

    getUserSubscriptionInvoicesRequest: PropTypes.func.isRequired,
    getUserSubscriptionInvoicesSuccess: PropTypes.func.isRequired,
    getUserSubscriptionInvoicesFailure: PropTypes.func.isRequired,

    getUserSubscriptionCreditNotesRequest: PropTypes.func.isRequired,
    getUserSubscriptionCreditNotesSuccess: PropTypes.func.isRequired,
    getUserSubscriptionCreditNotesFailure: PropTypes.func.isRequired,

    updatePaymentSourceRequest: PropTypes.func.isRequired,
    updatePaymentSourceSuccess: PropTypes.func.isRequired,
    updatePaymentSourceFailure: PropTypes.func.isRequired,

    getCouponDetailsRequest: PropTypes.func.isRequired,
    getCouponDetailsSuccess: PropTypes.func.isRequired,
    getCouponDetailsFailure: PropTypes.func.isRequired,

    updateVehicleFromDOMRequest: PropTypes.func.isRequired,
    updateVehicleFromDOMSuccess: PropTypes.func.isRequired,
    updateVehicleFromDOMFailure: PropTypes.func.isRequired,

    createSubscriptionV2Request: PropTypes.func.isRequired,
    createSubscriptionV2Success: PropTypes.func.isRequired,
    createSubscriptionV2Failure: PropTypes.func.isRequired,

    cancelSubscriptionRequest: PropTypes.func.isRequired,
    cancelSubscriptionSuccess: PropTypes.func.isRequired,
    cancelSubscriptionFailure: PropTypes.func.isRequired,

    printSubscriptionInvoiceRequest: PropTypes.func.isRequired,
    printSubscriptionInvoiceSuccess: PropTypes.func.isRequired,
    printSubscriptionInvoiceFailure: PropTypes.func.isRequired,

    printSubscriptionCreditNoteRequest: PropTypes.func.isRequired,
    printSubscriptionCreditNoteSuccess: PropTypes.func.isRequired,
    printSubscriptionCreditNoteFailure: PropTypes.func.isRequired,

    getAdyenPaymentMethodsRequest: PropTypes.func.isRequired,
    getAdyenPaymentMethodsSuccess: PropTypes.func.isRequired,
    getAdyenPaymentMethodsFailure: PropTypes.func.isRequired,
});
